import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import type { Identity } from 'spacetimedb';
import { tables as mpTables } from '@sdk/multiplayer';

/**
 * Subset of mp `presence_state` rows that represent onboarding agents
 * (actor_type === Agent) — i.e. the pool of identities a coordinator
 * can pick as a project's onboarding agent in Site Settings.
 *
 * The subscription is shared with PresenceHeartbeat (mounted globally
 * in main.tsx) so this hook reuses the existing live row stream with
 * no new connection or subscription. mp is org-scoped: one `{org}-mp`
 * serves every project, so the agent pool returned here is org-wide,
 * not per-site. The picker filters by selection, not by current
 * project.
 *
 * `identityHex` is precomputed (via `userIdentity.toHexString()`) so
 * consumers don't repeat the conversion at the call site — both the
 * server's `set_project_agent` reducer arg and the row's stored
 * `agent_identity` are the hex string form today (Samuel's
 * 2026-06-17 schema change to Identity-typed args hasn't reached the
 * portal bindings yet; when it does, callers swap to `userIdentity`).
 *
 * Sort order: online agents first (so the most likely picks land at
 * the top), then by display name within each group.
 */
export type AvailableAgent = {
  userIdentity: Identity;
  identityHex: string;
  displayName: string;
  isConnected: boolean;
};

export function useAvailableAgents(): {
  agents: AvailableAgent[];
  isReady: boolean;
} {
  const [rows, isReady] = useTable('mp', mpTables.presence_state);

  const agents = useMemo<AvailableAgent[]>(() => {
    const filtered = rows.filter((r) => r.actorType.tag === 'Agent');
    const mapped: AvailableAgent[] = filtered.map((r) => ({
      userIdentity: r.userIdentity,
      identityHex: r.userIdentity.toHexString(),
      displayName: r.displayName,
      isConnected: r.isConnected,
    }));
    mapped.sort((a, b) => {
      if (a.isConnected !== b.isConnected) return a.isConnected ? -1 : 1;
      return a.displayName.localeCompare(b.displayName);
    });
    return mapped;
  }, [rows]);

  return { agents, isReady };
}
